<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'

import BaseButton from '@/components/base/BaseButton.vue'
import BaseIcon from '@/components/base/BaseIcon.vue'
import { useTheme } from '@/composables/theme/use-theme.js'

import UserMenu from './UserMenu.vue'

/**
 * Breadcrumb, theme toggle, the operator.
 *
 * The breadcrumb reads `meta.crumb` off the matched route rather than a parallel map: a
 * second table of paths-to-labels drifts from the route tree the first time a path changes,
 * and the breadcrumb is where that drift is least visible. `sectionLabel` is null for a
 * screen that belongs to no section — the account page — and then only the crumb shows.
 */
const props = defineProps<{ sectionLabel: string | null; navCollapsed: boolean }>()
const emit = defineEmits<{ 'toggle-nav': [] }>()

const route = useRoute()
const { theme, toggle } = useTheme()

const crumb = computed(() => (typeof route.meta.crumb === 'string' ? route.meta.crumb : props.sectionLabel ?? ''))
</script>

<template>
  <header class="flex h-14 shrink-0 items-center justify-between gap-4 border-b border-border-subtle bg-raised px-4 lg:px-6">
    <!--
      Only the way BACK. Hiding the section menu is a control on the menu itself; showing
      it again cannot be, because by then there is no menu to put it on. Hidden below
      `lg`, where the section nav is a strip of pills and there is nothing to collapse.
    -->
    <BaseButton
      v-if="navCollapsed"
      variant="ghost"
      class="hidden shrink-0 lg:inline-flex"
      aria-label="Show the section menu"
      title="Show the section menu"
      @click="emit('toggle-nav')"
    >
      <BaseIcon
        name="sidebar-simple"
        :size="18"
      />
    </BaseButton>

    <nav
      class="flex min-w-0 flex-1 items-center gap-1.5 text-caption"
      aria-label="Breadcrumb"
    >
      <template v-if="sectionLabel !== null">
        <span class="text-ink-muted">{{ sectionLabel }}</span>
        <BaseIcon
          name="caret-right"
          :size="14"
          class="text-ink-muted"
        />
      </template>
      <span
        class="truncate-cell font-medium text-ink"
        aria-current="page"
        >{{ crumb }}</span
      >
    </nav>

    <div class="flex items-center gap-3">
      <!-- Shows the state you would SWITCH TO: a moon while light, a sun while dark. -->
      <BaseButton
        variant="ghost"
        :aria-label="theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'"
        :title="theme === 'dark' ? 'Light mode' : 'Dark mode'"
        @click="toggle()"
      >
        <BaseIcon
          :name="theme === 'dark' ? 'sun' : 'moon'"
          :size="18"
        />
      </BaseButton>
      <UserMenu />
    </div>
  </header>
</template>
